import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Sparkles, Loader2, MousePointerClick, Package, Eye, Link2, Upload, MessageCircle, Send, Globe, ImageDown } from "lucide-react";
import { downloadStoryImage } from "@/lib/storyImage";
import { ClicksChart } from "@/components/ClicksChart";
import { TestimonialsManager } from "@/components/TestimonialsManager";
import { PromoKit } from "@/components/PromoKit";
import { PreOrdersManager } from "@/components/PreOrdersManager";
import { LeadsManager } from "@/components/LeadsManager";
import { TelegramSettings } from "@/components/TelegramSettings";
import { copyText } from "@/components/landing/ShareButtons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ withCredentials: true });

const emptyForm = {
  name: "", tagline: "", description: "", benefitsText: "",
  price_display: "", image_url: "", affiliate_url: "",
  cta_text: "Dapatkan Sekarang", active: true, order: 0,
  featured: false, promo_text: "", promo_ends_at: "", coming_soon: false,
};

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState([]);
  const [sources, setSources] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const res = await client.get(`${API}/auth/me`);
        setUser(res.data);
        setAuth(true);
      } catch {
        setAuth(false);
        navigate("/login");
      }
    };
    checkAuth();
  }, [location.state, navigate]);

  const loadData = async () => {
    const [p, s, d, t] = await Promise.all([
      client.get(`${API}/admin/products`),
      client.get(`${API}/admin/stats`),
      client.get(`${API}/admin/clicks-daily`),
      client.get(`${API}/admin/traffic-sources`),
    ]);
    setProducts(p.data);
    setStats(s.data);
    setDaily(d.data);
    setSources(t.data);
  };

  useEffect(() => {
    if (auth === true) loadData().catch(() => toast.error("Gagal memuat data"));
  }, [auth]);

  if (auth === null) {
    return (
      <div className="min-h-screen bg-[#FBF7EE] flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#A67C1E]">Memeriksa sesi...</p>
      </div>
    );
  }

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, tagline: p.tagline, description: p.description,
      benefitsText: (p.benefits || []).join("\n"),
      price_display: p.price_display, image_url: p.image_url,
      affiliate_url: p.affiliate_url, cta_text: p.cta_text,
      active: p.active, order: p.order || 0,
      featured: p.featured || false, promo_text: p.promo_text || "",
      promo_ends_at: p.promo_ends_at ? p.promo_ends_at.slice(0, 16) : "",
      coming_soon: p.coming_soon || false,
    });
    setDialogOpen(true);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran gambar maksimal 5MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await client.post(`${API}/admin/upload`, fd);
      setForm((f) => ({ ...f, image_url: res.data.url }));
      toast.success("Gambar berhasil diunggah");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah gambar");
    } finally {
      setUploading(false);
    }
  };

  const handleGenerate = async () => {
    if (!form.name.trim()) {
      toast.error("Isi nama produk terlebih dahulu");
      return;
    }
    setGenerating(true);
    try {
      const res = await client.post(`${API}/admin/generate-copy`, {
        name: form.name,
        hint: form.description || form.tagline,
      });
      setForm((f) => ({
        ...f,
        tagline: res.data.tagline,
        description: res.data.description,
        benefitsText: res.data.benefits.join("\n"),
        cta_text: res.data.cta_text || f.cta_text,
      }));
      toast.success("Copy penjualan berhasil dibuat oleh AI");
    } catch {
      toast.error("Gagal membuat copy. Coba lagi.");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || (!form.coming_soon && !form.affiliate_url.trim())) {
      toast.error(form.coming_soon ? "Nama produk wajib diisi" : "Nama produk dan link affiliate wajib diisi");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      tagline: form.tagline,
      description: form.description,
      benefits: form.benefitsText.split("\n").map((b) => b.trim()).filter(Boolean),
      price_display: form.price_display,
      image_url: form.image_url,
      affiliate_url: form.affiliate_url.trim(),
      cta_text: form.cta_text || "Dapatkan Sekarang",
      active: form.active,
      order: Number(form.order) || 0,
      featured: form.featured,
      promo_text: form.promo_text,
      promo_ends_at: form.promo_ends_at,
      coming_soon: form.coming_soon,
    };
    try {
      if (editing) {
        await client.put(`${API}/admin/products/${editing.product_id}`, payload);
        toast.success("Produk diperbarui");
      } else {
        await client.post(`${API}/admin/products`, payload);
        toast.success("Produk ditambahkan");
      }
      setDialogOpen(false);
      await loadData();
    } catch {
      toast.error("Gagal menyimpan produk");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Hapus produk "${p.name}"?`)) return;
    try {
      await client.delete(`${API}/admin/products/${p.product_id}`);
      toast.success("Produk dihapus");
      await loadData();
    } catch {
      toast.error("Gagal menghapus produk");
    }
  };

  const copyDetailLink = async (p) => {
    const url = `${window.location.origin}/api/share/${p.slug || p.product_id}?src=copy`;
    if (await copyText(url)) {
      toast.success("Link halaman produk disalin — siap disebar!");
    } else {
      toast.error("Gagal menyalin link");
    }
  };

  const makeStory = async (p) => {
    try {
      await downloadStoryImage(p);
      toast.success("Gambar Story IG terunduh — siap diposting!");
    } catch {
      toast.error("Gagal membuat gambar story");
    }
  };

  const handleLogout = async () => {
    await client.post(`${API}/auth/logout`).catch(() => {});
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FBF7EE] text-[#211B10]" data-testid="admin-dashboard">
      <header className="border-b border-[#211B10]/10 backdrop-blur-xl bg-[#FBF7EE]/85 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" data-testid="dashboard-logo" className="flex items-center gap-2.5">
              <img src="/nalura-logo.jpg" alt="Logo NALURA" className="w-9 h-9 object-contain mix-blend-multiply" />
              <span>
                <span className="font-display text-xl tracking-tight leading-none block">NALURA<span className="text-[#A67C1E]">.</span></span>
                <span className="font-mono text-[7px] uppercase tracking-[0.25em] text-[#A67C1E] block mt-0.5">Good Mind Good Life</span>
              </span>
            </Link>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#211B10]/60">Dashboard Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span data-testid="admin-user-name" className="hidden md:block text-xs text-[#211B10]/50">{user?.name || user?.email}</span>
            <Button data-testid="logout-button" variant="outline" size="sm" onClick={handleLogout} className="border-[#211B10]/15 bg-transparent hover:bg-[#211B10]/5 hover:text-[#A67C1E]">
              <LogOut className="w-4 h-4 mr-2" /> Keluar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {[
            { label: "Total Produk", value: stats?.total_products ?? "—", icon: Package, testid: "stat-total-products" },
            { label: "Produk Aktif", value: stats?.active_products ?? "—", icon: Eye, testid: "stat-active-products" },
            { label: "Total Klik CTA", value: stats?.total_clicks ?? "—", icon: MousePointerClick, testid: "stat-total-clicks" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="border border-[#211B10]/10 bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#211B10]/60">{s.label}</span>
                <s.icon className="w-4 h-4 text-[#A67C1E]" />
              </div>
              <p className="font-display text-4xl tracking-tight">{s.value}</p>
            </div>
          ))}
        </div>

        {daily.length > 0 && <ClicksChart data={daily} />}

        {sources.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="traffic-sources">
            {sources.map((s) => {
              const Icon = { whatsapp: MessageCircle, telegram: Send, copy: Link2, direct: Globe }[s.source];
              return (
                <div key={s.source} data-testid={`traffic-source-${s.source}`} className="border border-[#211B10]/10 bg-white p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#211B10]/60">{s.label}</span>
                    <Icon className="w-3.5 h-3.5 text-[#A67C1E]" />
                  </div>
                  <p className="font-display text-3xl tracking-tight">{s.clicks}</p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#211B10]/55 mt-1">klik masuk</p>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-3xl tracking-tight">Produk Affiliate</h2>
          <Button data-testid="add-product-button" onClick={openCreate} className="bg-[#A67C1E] text-[#FBF7EE] hover:bg-[#211B10] font-medium">
            <Plus className="w-4 h-4 mr-2" /> Tambah Produk
          </Button>
        </div>

        <div className="border border-[#211B10]/10 bg-white">
          <Table data-testid="products-table">
            <TableHeader>
              <TableRow className="border-[#211B10]/10 hover:bg-transparent">
                <TableHead className="text-[#211B10]/50">Produk</TableHead>
                <TableHead className="text-[#211B10]/50">Status</TableHead>
                <TableHead className="text-[#211B10]/50 text-right">Klik</TableHead>
                <TableHead className="text-[#211B10]/50 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.product_id} data-testid={`product-row-${p.product_id}`} className="border-[#211B10]/10 hover:bg-[#211B10]/[0.04]">
                  <TableCell>
                    <p className="font-medium text-sm">{p.name}</p>
                    <p className="text-xs text-[#211B10]/60 truncate max-w-[260px]">{p.tagline}</p>
                  </TableCell>
                  <TableCell>
                    <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${p.active ? "text-[#A67C1E] border-[#A67C1E]/40" : "text-[#211B10]/60 border-[#211B10]/15"}`}>
                      {p.active ? "Aktif" : "Nonaktif"}
                    </span>
                  </TableCell>
                  <TableCell data-testid={`product-clicks-${p.product_id}`} className="text-right font-display text-xl">{p.clicks || 0}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-2">
                      <Button data-testid={`story-download-${p.product_id}`} title="Unduh gambar Instagram Story" variant="outline" size="sm" onClick={() => makeStory(p)} className="border-[#211B10]/15 bg-transparent hover:bg-[#211B10]/5 hover:text-[#A67C1E]">
                        <ImageDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button data-testid={`copy-link-${p.product_id}`} title="Salin link halaman produk" variant="outline" size="sm" onClick={() => copyDetailLink(p)} className="border-[#211B10]/15 bg-transparent hover:bg-[#211B10]/5 hover:text-[#A67C1E]">
                        <Link2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button data-testid={`edit-product-${p.product_id}`} variant="outline" size="sm" onClick={() => openEdit(p)} className="border-[#211B10]/15 bg-transparent hover:bg-[#211B10]/5">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button data-testid={`delete-product-${p.product_id}`} variant="outline" size="sm" onClick={() => handleDelete(p)} className="border-[#211B10]/15 bg-transparent hover:bg-red-100 hover:text-red-700">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {products.length === 0 && (
                <TableRow className="border-[#211B10]/10 hover:bg-transparent">
                  <TableCell colSpan={4} className="text-center py-12 text-[#211B10]/60 text-sm" data-testid="products-table-empty">
                    Belum ada produk. Klik "Tambah Produk" untuk memulai.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <PreOrdersManager client={client} API={API} />

        <LeadsManager client={client} API={API} />

        <PromoKit products={products} />

        <TestimonialsManager client={client} API={API} />

        <TelegramSettings client={client} API={API} />
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="product-dialog" className="bg-white border-[#211B10]/10 text-[#211B10] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              {editing ? "Edit Produk" : "Tambah Produk Baru"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="p-name" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Nama Produk *</Label>
                <Input id="p-name" data-testid="product-name-input" value={form.name} onChange={set("name")} placeholder="cth: Brainboost Money Magnet" className="bg-[#FBF7EE] border-[#211B10]/15" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-price" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Harga (opsional)</Label>
                <Input id="p-price" data-testid="product-price-input" value={form.price_display} onChange={set("price_display")} placeholder="cth: Rp 297.000" className="bg-[#FBF7EE] border-[#211B10]/15" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="p-url" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Link Affiliate {form.coming_soon ? "(opsional saat Segera Hadir)" : "*"}</Label>
              <Input id="p-url" data-testid="product-affiliate-input" value={form.affiliate_url} onChange={set("affiliate_url")} placeholder="https://..." className="bg-[#FBF7EE] border-[#211B10]/15" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="p-image" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Gambar Produk</Label>
              <div className="flex gap-3">
                <Input id="p-image" data-testid="product-image-input" value={form.image_url} onChange={set("image_url")} placeholder="https://... atau unggah file" className="bg-[#FBF7EE] border-[#211B10]/15 flex-1" />
                <input type="file" id="p-image-file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" />
                <Button
                  type="button"
                  data-testid="upload-image-button"
                  variant="outline"
                  disabled={uploading}
                  onClick={() => document.getElementById("p-image-file").click()}
                  className="border-[#A67C1E]/50 text-[#A67C1E] bg-transparent hover:bg-[#A67C1E]/10 shrink-0"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Unggah
                </Button>
              </div>
              {form.image_url && (
                <img src={form.image_url} alt="Pratinjau" data-testid="image-preview" className="mt-2 h-24 w-32 object-cover border border-[#211B10]/10" />
              )}
            </div>

            <div className="border border-[#A67C1E]/30 bg-[#A67C1E]/5 p-4 flex items-center justify-between gap-4">
              <p className="text-xs text-[#211B10]/60 leading-relaxed">
                Biarkan AI menulis tagline, deskripsi, manfaat, dan teks tombol secara otomatis berdasarkan nama produk.
              </p>
              <Button data-testid="ai-generate-button" onClick={handleGenerate} disabled={generating} variant="outline" className="border-[#A67C1E]/50 text-[#A67C1E] bg-transparent hover:bg-[#A67C1E]/10 shrink-0">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Buat dengan AI
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="p-tagline" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Tagline</Label>
              <Input id="p-tagline" data-testid="product-tagline-input" value={form.tagline} onChange={set("tagline")} className="bg-[#FBF7EE] border-[#211B10]/15" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="p-desc" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Deskripsi</Label>
              <Textarea id="p-desc" data-testid="product-description-input" value={form.description} onChange={set("description")} rows={4} className="bg-[#FBF7EE] border-[#211B10]/15" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="p-benefits" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Manfaat (satu per baris)</Label>
              <Textarea id="p-benefits" data-testid="product-benefits-input" value={form.benefitsText} onChange={set("benefitsText")} rows={4} className="bg-[#FBF7EE] border-[#211B10]/15" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-end">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="p-cta" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Teks Tombol (CTA)</Label>
                <Input id="p-cta" data-testid="product-cta-input" value={form.cta_text} onChange={set("cta_text")} className="bg-[#FBF7EE] border-[#211B10]/15" />
              </div>
              <div className="flex items-center gap-3 pb-2">
                <Switch id="p-active" data-testid="product-active-switch" checked={form.active} onCheckedChange={(v) => set("active")(v)} />
                <Label htmlFor="p-active" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Aktif</Label>
              </div>
              <div className="flex items-center gap-3 pb-2">
                <Switch id="p-coming-soon" data-testid="product-coming-soon-switch" checked={form.coming_soon} onCheckedChange={(v) => set("coming_soon")(v)} />
                <Label htmlFor="p-coming-soon" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Segera Hadir (tanpa tombol beli)</Label>
              </div>
            </div>

            <div className="border border-[#A67C1E]/30 bg-[#A67C1E]/5 p-4 space-y-4">
              <div className="flex items-center gap-3">
                <Switch id="p-featured" data-testid="product-featured-switch" checked={form.featured} onCheckedChange={(v) => set("featured")(v)} />
                <Label htmlFor="p-featured" className="text-xs uppercase tracking-[0.15em] text-[#A67C1E]">Jadikan Kupon Sorotan (banner promo di beranda)</Label>
              </div>
              {form.featured && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="p-promo" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Teks Promo Banner</Label>
                    <Input id="p-promo" data-testid="product-promo-input" value={form.promo_text} onChange={set("promo_text")} placeholder="mis. Penawaran terbaik minggu ini — mulai perubahan Anda hari ini" className="bg-[#FBF7EE] border-[#211B10]/15" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="p-promo-ends" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Promo Berakhir Pada (hitung mundur, opsional)</Label>
                    <Input id="p-promo-ends" data-testid="product-promo-ends-input" type="datetime-local" value={form.promo_ends_at} onChange={set("promo_ends_at")} className="bg-[#FBF7EE] border-[#211B10]/15" />
                    <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#211B10]/55">Kosongkan jika tanpa batas waktu</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button data-testid="product-cancel-button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#211B10]/15 bg-transparent hover:bg-[#211B10]/5">
              Batal
            </Button>
            <Button data-testid="product-save-button" onClick={handleSave} disabled={saving} className="bg-[#A67C1E] text-[#FBF7EE] hover:bg-[#211B10] font-medium">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Simpan Perubahan" : "Tambah Produk"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
